'use client';
/**
 * @brisa/ui · ConsentCheckbox (facelift v1)
 *
 * Checkbox de consentimento para fluxo `/app/auth/consent`.
 *
 * Tipos:
 *   - legal: copy assertiva + icon secondary button na **extremidade direita
 *     do box** → abre LegalDrawer (TOS, Privacy).
 *   - age: ícone calendário lucide leading no label.
 *   - optional: descrição inline (marketing).
 *
 * Visual facelift v1 (W3.2 batch consent — decisão PL 2026-05-23):
 *   - Box 20×20 radius-sm (8px), border 1.5px text-16, bg transparente.
 *   - Checked: bg accent-yellow + check preto 12px + border yellow.
 *   - Hover (unchecked): bg text-08, transition base.
 *   - Focus-visible: ring 2px accent-yellow + offset 2px (peer pattern).
 *   - Disabled: opacity-50, no pointer events.
 *   - Label title: Archivo 14px font-medium text-100 (black), leading-relaxed.
 *   - Description: Archivo 12px text-64, leading-relaxed.
 *   - Doc link (legal): Button variant=secondary-pill (pill dark + arrow amarelo)
 *     28×28 pinned na extremidade direita do box, alinhado ao top do checkbox
 *     via self-start + mt-0.5 (decisão PL 2026-05-23 — substituiu "→" sublinhado
 *     inline, e depois reposicionado pra extremidade direita).
 *
 * API 100% retrocompat — apenas o markup/CSS mudou. Consumers
 * (ConsentClient) não precisam alterar import nem props.
 *
 * Acessibilidade:
 *   - <input> sr-only com role="checkbox" — keyboard nav + screen readers funcionam.
 *   - aria-checked sincronizado com state.
 *   - aria-describedby aponta para description quando presente.
 *   - Doc link tem aria-label completo (openDocumentLabel).
 *   - Focus ring visível no box via peer-focus-visible pattern.
 */
import * as React from 'react';
import { ArrowRight, Calendar as CalendarIcon } from 'lucide-react';

import { Button } from '../../ui/button';

export type ConsentType = 'legal' | 'age' | 'optional';

export interface ConsentCheckboxProps {
  type: ConsentType;
  label: string;
  description?: string;
  documentName?: 'tos' | 'privacy';
  version?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  onOpenDocument?: (doc: 'tos' | 'privacy') => void;
  openDocumentLabel?: string;
  dataEvent?: string;
  disabled?: boolean;
}

export function ConsentCheckbox({
  type,
  label,
  description,
  documentName,
  version = 'v1.0',
  checked,
  onChange,
  onOpenDocument,
  openDocumentLabel,
  dataEvent,
  disabled = false,
}: ConsentCheckboxProps) {
  const id = React.useId();
  const descId = description ? `${id}-desc` : undefined;
  return (
    <label
      htmlFor={id}
      data-consent-type={type}
      data-checked={checked}
      className={[
        'flex w-full items-start gap-3 py-2',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Input nativo sr-only — habilita peer-focus-visible no box e mantém
          a11y (keyboard + screen reader). DEVE vir antes do box no DOM. */}
      <input
        id={id}
        type="checkbox"
        role="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        aria-checked={checked}
        aria-describedby={descId}
        data-event={dataEvent}
        data-consent-version={version}
        className="peer sr-only"
      />

      {/* Box visual — 20×20 radius-sm. Checked: bg amarelo + check preto.
          Hover unchecked: bg text-08 subtle. Focus: ring amarelo via peer. */}
      <span
        aria-hidden="true"
        className={[
          'mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center',
          'rounded-[var(--radius-sm)] border-[1.5px] transition-colors duration-base ease-out',
          'peer-focus-visible:ring-2 peer-focus-visible:ring-[hsl(var(--accent-yellow))] peer-focus-visible:ring-offset-2',
          checked
            ? 'border-[hsl(var(--accent-yellow))] bg-[hsl(var(--accent-yellow))]'
            : 'border-[hsl(var(--text-16))] bg-transparent hover:bg-[hsl(var(--text-08))]',
        ].join(' ')}
      >
        {checked && (
          <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
            <path
              d="m2.5 6 2.5 2.5L9.5 3.5"
              stroke="black"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        )}
      </span>

      {/* Label + description — flex-1 empurra o doc button pra extremidade
          direita via flex-justify-between implícito do label container. */}
      <span className="flex flex-1 flex-col gap-1">
        <span className="flex items-center gap-2 font-body text-sm font-medium leading-relaxed text-black">
          {type === 'age' && (
            <CalendarIcon
              size={14}
              className="text-[hsl(var(--text-64))]"
              aria-hidden="true"
            />
          )}
          <span>{label}</span>
        </span>
        {description && (
          <span
            id={descId}
            className="font-body text-xs leading-relaxed text-[hsl(var(--text-64))]"
          >
            {description}
          </span>
        )}
      </span>

      {/* Doc button — sempre na extremidade direita do box (3º flex item do
          <label>, empurrado por flex-1 do label content). Self-start alinha
          verticalmente com o checkbox box e a primeira linha do label. */}
      {type === 'legal' && documentName && (
        <Button
          type="button"
          variant="secondary-pill"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpenDocument?.(documentName);
          }}
          className="mt-0.5 h-7 w-7 shrink-0 self-start p-0"
          aria-label={openDocumentLabel ?? `Abrir ${documentName}`}
        >
          <ArrowRight size={12} aria-hidden="true" />
        </Button>
      )}
    </label>
  );
}
